import { useCallback, useEffect, useState } from 'react';
import { DezoomDots } from '../DezoomDots';
import type { PhotoAttributionData } from '../PhotoAttribution';
import { PlayZoomStage } from '../PlayZoomStage';
import { ZoomImage } from '../ZoomImage';
import { t } from '../../hooks/useT';
import { preloadImages } from '../../utils/imageCache';
import type { CropMeta } from '../../utils/cropUtils';
import { LiveCropView } from './LiveCropView';

interface ZoomLevel {
  level: number;
  imageUrl: string;
  cropMeta?: CropMeta;
}

interface PreviewPuzzle {
  id: string;
  puzzleNumber: number;
  answerCanonical: string;
  imageVersion?: number;
  originalImageUrl?: string | null;
  zoomLevels: ZoomLevel[];
  attribution?: PhotoAttributionData | null;
}

interface AdminPuzzlePreviewProps {
  puzzle: PreviewPuzzle;
  onClose: () => void;
}

export function AdminPuzzlePreview({ puzzle, onClose }: AdminPuzzlePreviewProps) {
  const maxLevel = Math.max(0, ...puzzle.zoomLevels.map((z) => z.level));
  const [zoomLevel, setZoomLevel] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);

  const activeZoom = puzzle.zoomLevels.find((z) => z.level === zoomLevel) || puzzle.zoomLevels[0];
  const canLivePreview = !!(puzzle.originalImageUrl && activeZoom?.cropMeta && imgSize);

  useEffect(() => {
    preloadImages(puzzle.zoomLevels.map((z) => z.imageUrl));
    setZoomLevel(0);
    setShowAnswer(false);
  }, [puzzle.id, puzzle.imageVersion, puzzle.zoomLevels]);

  useEffect(() => {
    if (!puzzle.originalImageUrl) {
      setImgSize(null);
      return;
    }
    const img = new Image();
    img.onload = () => setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = puzzle.originalImageUrl;
  }, [puzzle.originalImageUrl, puzzle.imageVersion]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleDezoom = () => {
    setZoomLevel((lvl) => Math.min(lvl + 1, maxLevel));
  };

  const handleReset = () => {
    setZoomLevel(0);
    setShowAnswer(false);
  };

  const atMaxZoom = zoomLevel >= maxLevel;

  return (
    <div className="admin-play-preview" role="dialog" aria-modal="true" aria-label={t('admin.puzzles.preview')}>
      <header className="play-header">
        <div className="play-header-left">
          <span className="puzzle-num">#{puzzle.puzzleNumber}</span>
          <button type="button" className="btn secondary admin-btn-sm" onClick={onClose}>
            {t('admin.puzzles.previewClose')}
          </button>
        </div>
        <DezoomDots used={zoomLevel} total={maxLevel + 1} />
      </header>

      <PlayZoomStage revealed>
        {canLivePreview && activeZoom?.cropMeta && imgSize ? (
          <LiveCropView
            originalUrl={puzzle.originalImageUrl!}
            crop={activeZoom.cropMeta}
            imgW={imgSize.w}
            imgH={imgSize.h}
          />
        ) : (
          <ZoomImage
            levels={puzzle.zoomLevels}
            activeLevel={zoomLevel}
            attribution={puzzle.attribution}
          />
        )}
      </PlayZoomStage>

      <footer className="play-footer">
        <div className="admin-preview-actions">
          <button
            type="button"
            className="btn primary"
            onClick={handleDezoom}
            disabled={atMaxZoom}
          >
            {t('admin.puzzles.previewDezoom')}
          </button>
          <button type="button" className="btn secondary" onClick={handleReset}>
            {t('admin.puzzles.previewReset')}
          </button>
          <button
            type="button"
            className="btn secondary"
            onClick={() => setShowAnswer((v) => !v)}
          >
            {showAnswer ? t('admin.puzzles.previewHideAnswer') : t('admin.puzzles.previewShowAnswer')}
          </button>
        </div>
        {showAnswer && (
          <p className="admin-preview-answer">
            {t('admin.puzzles.answer')} <strong>{puzzle.answerCanonical}</strong>
          </p>
        )}
      </footer>
    </div>
  );
}
